import { Boss, BossDef, BossAttack } from './boss';
import { GAME, ASSETS } from '../config';

// ── 期中總魔・三相之劫 (shi-zhong) ────────────────────────────────────────────
// boss-tougher (boss 2026-06-13「魔王要夠強,太弱沒挑戰性」): 期中王 HP 800→2600(更肉,
//   tier3 > tier2),phase2HpThreshold 同步抬到 1300。phaseThresholds 是 HP 分數(不動)→
//   三相邊界隨高 HP 等比例縮放,節奏不變;可躲鐵則:招式/子彈速度/安全縫一律不動。
// HP 2600, 3-phase mech overlord
//   Phase 1 (HP > ~1736 ≈ 66.75 %): celestial — single-shot + fan
//   Phase 2 (~868–1736 ≈ 33.37–66.75 %): runic — silence-player + spawn-minions
//   Phase 3 (HP < ~868 ≈ 33.37 %): elemental — freeze-player + 9-bullet fan

const SHI_ZHONG: BossDef = {
  id: 'shi-zhong',
  displayName: '期中總魔・三相之劫',
  hp: 2600,
  speedPxPerSec: 35,
  // boss-behavior: 期中王更巨大 128→184 (×1.44).
  size: 184,
  color: 0x7b1fa2,
  assetKey: ASSETS.bossShiZhong,
  shape: 'hexagon',
  faction: 'celestial',
  affinityFaction: 'runic', // 三相之劫 cycles celestial/runic/elemental; runic (silence) is its signature phase
  tier: 3,
  attackACooldownMs: 1800,
  attackBCooldownMs: 5000,
  // phaseThresholds supersedes phase2HpThreshold; kept for backward compat
  phase2HpThreshold: 1300,
  phaseThresholds: [0.6675, 0.3337], // ~1736 / ~868 HP boundaries (HP 分數不動)
  attackBWarningMs: 1000,
  introDialog: '三相之劫,你準備好了嗎?',
  arenaTint: 0x1a0a0a,
  entranceCinematic: 'phase-in' as const,
  deathCinematic: 'fragment-into-pieces' as const,
  uniqueGimmick: 'phase-shield' as const,

  tickAttacks(self: Boss, nowMs: number, playerX: number): BossAttack[] {
    const out: BossAttack[] = [];
    if (self.y < self.def.size / 2) return out;

    const ph = self.phase;

    if (nowMs - self.lastAttackAMs >= self.effectiveAttackACooldownMs) {
      self.lastAttackAMs = nowMs;
      const angle = Math.atan2(playerX - self.x, GAME.height * 0.85 - self.y);

      if (ph === 1) {
        // Phase 1 — celestial: aimed single-shot + narrow fan
        out.push({ kind: 'single-shot', angleRad: angle, speedPxPerSec: 380, damage: 18 });
        out.push({ kind: 'fan-shot', angles: [angle - 0.2, angle + 0.2], speedPxPerSec: 320, damage: 12 });
      } else if (ph === 2) {
        // Phase 2 — runic: silence + wide fan (boss-behavior: 3 → 7 道 + 追蹤彈)
        // boss-phase2-transform (老闆「攻擊方式要改變 動態變換」):phase2 質變 —
        //   除既有 silence/fan/homing 外,新增 magic-bolt 符文波 (phase1 純直線單發+窄扇,
        //   從不放波浪魔法彈),讓三相之劫進入符文相時攻擊形態明顯不同 (波形+追蹤+沉默)。
        out.push({ kind: 'silence-player', durationMs: 600 });
        out.push({ kind: 'fan-shot', angles: [-0.6, -0.4, -0.2, 0, 0.2, 0.4, 0.6], speedPxPerSec: 300, damage: 12 });
        out.push({ kind: 'homing-shot', count: 3, speedPxPerSec: 225, turnRatePerSec: 120, damage: 12 });
        out.push({ kind: 'magic-bolt', count: 4, speedPxPerSec: 210, amplitudePx: 46, periodMs: 880, damage: 9 });
      } else {
        // Phase 3 — elemental: freeze + wide fan (boss-behavior: 5 → 9 道 + 更兇追蹤彈)
        out.push({ kind: 'freeze-player', durationMs: 400 });
        out.push({ kind: 'fan-shot', angles: [-0.8, -0.6, -0.4, -0.2, 0, 0.2, 0.4, 0.6, 0.8], speedPxPerSec: 280, damage: 9 });
        out.push({ kind: 'homing-shot', count: 4, speedPxPerSec: 240, turnRatePerSec: 140, damage: 11 });
      }
    }

    if (nowMs - self.lastAttackBMs >= self.effectiveAttackBCooldownMs) {
      self.lastAttackBMs = nowMs;

      if (ph === 1) {
        // Phase 1 B: extra aimed shot
        const angle = Math.atan2(playerX - self.x, GAME.height * 0.85 - self.y);
        out.push({ kind: 'single-shot', angleRad: angle, speedPxPerSec: 420, damage: 22 });
      } else if (ph === 2) {
        // Phase 2 B: spawn minions
        out.push({ kind: 'spawn-minions', count: 4 });
      } else {
        // Phase 3 B: burst fan (boss-behavior: 5 → 9 道)
        out.push({ kind: 'fan-shot', angles: [-0.8, -0.6, -0.4, -0.2, 0, 0.2, 0.4, 0.6, 0.8], speedPxPerSec: 340, damage: 14 });
      }
    }

    return out;
  },
};

// ── 期末王・天命召喚者 (tian-ming) ────────────────────────────────────────────
// boss-tougher (boss 2026-06-13「魔王要夠強,太弱沒挑戰性」): 期末王 HP 1200→3800(最肉,
//   全 roster 最高,呼應「期末總驗收」要靠學習練滿戰力才打得贏),phase2HpThreshold→1900。
//   phaseThresholds 是 HP 分數(不動)→ 四相邊界隨高 HP 等比例縮放,節奏不變。
//   可躲鐵則:招式種類/子彈速度/安全縫一律不動,只調 hp / phase2 門檻。
// HP 3800, 4-phase — one faction per phase
//   Phase 1 (HP > 2850 = 75 %): celestial — precision shots
//   Phase 2 (HP 1900–2850 = 50–75 %): runic — silence + minions
//   Phase 3 (HP 950–1900 = 25–50 %): techno/elemental hybrid — freeze + rapid fan
//   Phase 4 (HP < 950 = 25 %): dominion — silence-wave + all-faction barrage
// On death → grants unlockedSkills entry 'legendary-tian-ming-summon'

const TIAN_MING: BossDef = {
  id: 'tian-ming',
  displayName: '期末王・天命召喚者',
  hp: 3800,
  speedPxPerSec: 30,
  // boss-behavior: 期末王最巨大 144→200 (×1.39). 800x600 場景上限,別擋滿畫面.
  size: 200,
  color: 0x4a0080,
  assetKey: ASSETS.bossTianMing,
  shape: 'double-circle',
  faction: 'celestial',
  affinityFaction: 'celestial', // 天命召喚者 = fate summoner; commands all factions; primary identity = celestial
  tier: 3,
  attackACooldownMs: 1600,
  attackBCooldownMs: 4500,
  // phaseThresholds supersedes phase2HpThreshold; kept for backward compat
  phase2HpThreshold: 1900,
  phaseThresholds: [0.75, 0.5, 0.25], // 2850 / 1900 / 950 HP boundaries (HP 分數不動)
  attackBWarningMs: 1200,
  introDialog: '天命召喚,五門派齊聚此戰!',
  arenaTint: 0x00050a,
  entranceCinematic: 'rise-from-ground' as const,
  deathCinematic: 'crystal-shatter' as const,
  uniqueGimmick: 'mirror-player' as const,

  tickAttacks(self: Boss, nowMs: number, playerX: number): BossAttack[] {
    const out: BossAttack[] = [];
    if (self.y < self.def.size / 2) return out;

    const ph = self.phase;

    if (nowMs - self.lastAttackAMs >= self.effectiveAttackACooldownMs) {
      self.lastAttackAMs = nowMs;
      const angle = Math.atan2(playerX - self.x, GAME.height * 0.85 - self.y);

      if (ph === 1) {
        // Phase 1 — celestial: precision triple aimed
        out.push({ kind: 'single-shot', angleRad: angle, speedPxPerSec: 400, damage: 20 });
        out.push({ kind: 'single-shot', angleRad: angle - 0.1, speedPxPerSec: 380, damage: 16 });
        out.push({ kind: 'single-shot', angleRad: angle + 0.1, speedPxPerSec: 380, damage: 16 });
      } else if (ph === 2) {
        // Phase 2 — runic: silence + 3-fan
        // boss-phase2-transform (老闆「攻擊方式要改變 動態變換」):phase2 質變 —
        //   phase1 是純精準三連發直線彈;進入符文相新增 magic-bolt 波浪魔法彈 (新形態),
        //   攻擊從「直線狙擊」轉成「沉默+符文波鋪面」,玩家明顯感覺天命召喚換了打法。
        out.push({ kind: 'silence-player', durationMs: 700 });
        out.push({ kind: 'fan-shot', angles: [-0.25, 0, 0.25], speedPxPerSec: 320, damage: 15 });
        out.push({ kind: 'magic-bolt', count: 4, speedPxPerSec: 210, amplitudePx: 48, periodMs: 880, damage: 10 });
      } else if (ph === 3) {
        // Phase 3 — techno/elemental: freeze + rapid wide fan + 追蹤彈 (boss-behavior)
        out.push({ kind: 'freeze-player', durationMs: 350 });
        out.push({ kind: 'fan-shot', angles: [-0.6, -0.4, -0.2, 0, 0.2, 0.4, 0.6, 0.8], speedPxPerSec: 360, damage: 11 });
        out.push({ kind: 'homing-shot', count: 4, speedPxPerSec: 235, turnRatePerSec: 130, damage: 12 });
      } else {
        // Phase 4 — dominion: silence + barrage from all angles + 更兇追蹤彈 (boss-behavior)
        out.push({ kind: 'silence-player', durationMs: 800 });
        out.push({ kind: 'fan-shot', angles: [-0.8, -0.6, -0.4, -0.2, 0, 0.2, 0.4, 0.6, 0.8], speedPxPerSec: 350, damage: 14 });
        out.push({ kind: 'homing-shot', count: 5, speedPxPerSec: 245, turnRatePerSec: 145, damage: 14 });
      }
    }

    if (nowMs - self.lastAttackBMs >= self.effectiveAttackBCooldownMs) {
      self.lastAttackBMs = nowMs;

      if (ph === 1) {
        // Phase 1 B: spawn 3 minions
        out.push({ kind: 'spawn-minions', count: 3 });
      } else if (ph === 2) {
        // Phase 2 B: silence-wave (wide suppression)
        out.push({ kind: 'silence-wave', radiusPx: 300, durationMs: 900 });
      } else if (ph === 3) {
        // Phase 3 B: freeze + spawn fast minions
        out.push({ kind: 'freeze-player', durationMs: 500 });
        out.push({ kind: 'spawn-minions', count: 5, bonusSpeed: 60 });
      } else {
        // Phase 4 B: ultimate 11-bullet fan (boss-behavior: 7 → 11 道, 期末終局彈幕)
        out.push({ kind: 'fan-shot', angles: [-0.85, -0.68, -0.51, -0.34, -0.17, 0, 0.17, 0.34, 0.51, 0.68, 0.85], speedPxPerSec: 400, damage: 18 });
      }
    }

    return out;
  },
};

/** Tier-3 boss pool: 期中 + 期末 semester bosses. */
export const BOSS_TIER3_POOL: BossDef[] = [SHI_ZHONG, TIAN_MING];

/** Pick a random Tier-3 boss. */
export function pickRandomTier3Boss(seedRandom: () => number = Math.random): BossDef {
  return BOSS_TIER3_POOL[Math.floor(seedRandom() * BOSS_TIER3_POOL.length)];
}

/** Select a specific Tier-3 boss by role. */
export function getTier3Boss(level: 'midterm' | 'final'): BossDef {
  return level === 'midterm' ? SHI_ZHONG : TIAN_MING;
}

/**
 * Skill ID granted when 天命召喚者 is defeated.
 * BattleScene should push this into progress.unlockedSkills on boss death
 * when bossDef.id === 'tian-ming'.
 */
export const LEGENDARY_TIAN_MING_SKILL_ID = 'legendary-tian-ming-summon';
